import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Action, Store } from '@ngrx/store';
import { FlightSubBookingPayload } from '@shared/models/flight-sub-booking-payload.model';
import { FlightSubBookingResponse } from '@shared/models/flight-sub-booking-response.model';
import { SiteSettingsService } from '@shared/services/site-settings.service';
import { ApiEndpoint } from '@shared/shared/enums/api-endpoints.enum';
import { ISiteSettings } from '@shared/shared/interfaces/site-settings.interfaces';
import { getFlightSearchState } from '@shared/store/travel-search';
import { FlightSearchState } from '@shared/store/travel-search/reducers/flight-search.reducers';
import * as UrlLib from '@shared/utils/url.lib';
import { TRAVEL_FLIGHT_SUB_BOOKING_LOCAL_STORAGE_KEY, TRAVEL_SELECTED_FLIGHT_LOCAL_STORAGE_KEY } from 'app.config';
import { Observable, of } from 'rxjs';
import { catchError, filter, map, switchMap, take, tap, withLatestFrom } from 'rxjs/operators';
import * as travelBookingProgressBarActions from '../actions/travel-booking-progress-bar.actions';
import { TravellerInfoResponse } from '../actions/travel-booking.actions';
import * as fromActions from '../actions/travel-flight-sub-booking.actions';
import { TravelBookingState } from '../reducers';
import * as fromReducers from '../reducers/travel-flight-sub-booking.reducers';
import { GetTravelBookingBarState, getTravelFlightSubBooking } from '../selectors';

@Injectable()
export class TravelFlightSubBookingEffects {
  private fareFamilySwitch: boolean;
  private fareFamilySwitchLoaded: boolean;

  constructor(
    private actions$: Actions,
    private http: HttpClient,
    private ngrxStore: Store<{ 'travel-booking': TravelBookingState }>,
    private siteSettingsService: SiteSettingsService,
    private router: Router
  ) {
    this.loadFareFamilySwitch();
  }

  public ngrxOnInitEffects(): Action {
    return new fromActions.ViewFlightSubBooking();
  }

  private loadFareFamilySwitch() {
    if (this.fareFamilySwitchLoaded) return;

    this.fareFamilySwitchLoaded = false;
    this.siteSettingsService
      .fetchSiteSettings()
      .pipe(
        filter((siteSettings: ISiteSettings) => this.siteSettingsService.settingsRetrieved && !!siteSettings.id),
        take(1)
      )
      .subscribe((siteSettings: ISiteSettings) => {
        this.fareFamilySwitch = siteSettings.switches.travel_fare_families;
        this.fareFamilySwitchLoaded = true;
      });
  }

  public clearFlightSubBooking$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(fromActions.CLEAR_FLIGHT_SUB_BOOKING),
        tap(() => {
          localStorage.removeItem(TRAVEL_FLIGHT_SUB_BOOKING_LOCAL_STORAGE_KEY);
          localStorage.removeItem(TRAVEL_SELECTED_FLIGHT_LOCAL_STORAGE_KEY);
        })
      ),
    { dispatch: false }
  );

  public createFlightSubBooking$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromActions.CREATE_FLIGHT_SUB_BOOKING),
      withLatestFrom(this.ngrxStore.select(getFlightSearchState)),
      switchMap(([, flightSearchState]) => {
        let endPoint = ApiEndpoint.TRAVEL_FLIGHT_SUB_BOOKING;
        this.loadFareFamilySwitch();
        const payload = this.createFlightSubBookingData(flightSearchState);
        if (this.fareFamilySwitch && payload.fare_families_exist) {
          endPoint = ApiEndpoint.TRAVEL_FLIGHT_FARE_FAMILY_SUB_BOOKING;
        }

        return this.http.post<FlightSubBookingResponse>(endPoint, payload).pipe(
          map((subBooking) => {
            this.createSubBookingSuccess(subBooking);
            this.routeToNextPage();
            return new fromActions.CreateFlightSubBookingSuccess(subBooking);
          }),
          catchError((err) => of(new fromActions.CreateFlightSubBookingFailure(err)))
        );
      })
    )
  );

  public patchFlightSubBookingTravellers$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromActions.PATCH_FLIGHT_SUB_BOOKING_TRAVELLERS),
      withLatestFrom(this.ngrxStore.select(getTravelFlightSubBooking)),
      switchMap(([action, subBooking]: [fromActions.PatchFlightSubBookingTravellers, any]) => {
        const url = UrlLib.replaceTemplateKeys(ApiEndpoint.TRAVEL_FLIGHT_SUB_BOOKING_PATCH_TRAVELLERS, {
          id: subBooking.id,
        });
        const payload = this.createPatchPayload(action.payload);
        return this.http.patch<TravellerInfoResponse>(url, payload).pipe(
          map((result) => {
            const names = [];
            const docRequirements = [];
            payload.travellers.forEach((travellerId: number) => {
              const foundIndex = result.travellers.findIndex((traveller) => traveller === travellerId);
              names.push(result.travellers_names[foundIndex]);
              const docReq = result.travellers_documents_requirements.find((doc) =>
                doc.traveller.includes(travellerId)
              );
              if (docReq) {
                docRequirements.push(docReq);
              }
            });
            return new fromActions.PatchFlightSubBookingTravellersSuccess({
              ...result,
              travellers_names: names,
              travellers_documents_requirements: docRequirements,
            });
          }),
          catchError((err) => of(new fromActions.PatchFlightSubBookingTravellersFailure(err)))
        );
      })
    )
  );

  private createPatchPayload(payload: any): any {
    const travellerIds = Object.keys(payload).reduce(
      (res, key) => (/traveller\d/.test(key) || /leadTraveller/.test(key) ? [...res, payload[key]] : res),
      []
    );
    return {
      travellers: travellerIds,
      contact_phone_number: payload.country.calling_code + payload.phone,
      contact_email: payload.email,
    };
  }

  private createFlightSubBookingData(flightSearchState: FlightSearchState): FlightSubBookingPayload {
    const { selectedFareFamily, selectedFlight, search, searchReferenceId, airlines, locations } = flightSearchState;
    const { itinerary, legs, segments } = selectedFlight;
    const outboundLeg = legs[0];
    const inboundLeg = legs[1];
    const firstOutboundSegment = segments[outboundLeg.segments[0]];
    const firstInboundSegment = inboundLeg ? segments[inboundLeg.segments[0]] : null;
    const fareFamily =
      selectedFlight.itinerary.fare_families_exist && selectedFareFamily ? selectedFareFamily.name : '';
    const inboundStartLocation = inboundLeg ? locations.entities[inboundLeg.start_location_id] : null;
    const inboundEndLocation = inboundLeg ? locations.entities[inboundLeg.end_location_id] : null;
    const outboundStartLocation = outboundLeg ? locations.entities[outboundLeg.start_location_id] : null;
    const outboundEndLocation = outboundLeg ? locations.entities[outboundLeg.end_location_id] : null;

    return {
      flight_class_id: `${firstOutboundSegment.flight_class}`,
      booking_token: itinerary.booking_reference,
      inbound_airline_iata_code: inboundLeg ? airlines.entities[firstInboundSegment.operating_airline_id].code : '',
      inbound_arrival_airport_iata_code: inboundLeg ? inboundEndLocation.iata_code : '',
      inbound_departure_airport_iata_code: inboundLeg ? inboundStartLocation.iata_code : '',
      inbound_departure_datetime: inboundLeg ? firstInboundSegment.start_datetime : '',
      inbound_flight_code: inboundLeg ? firstInboundSegment.marketing_flight_number : '',
      num_adults: `${search.num_adults}`,
      num_young_adults: `${search.num_young_adults}`,
      num_children: `${search.children_ages.length}`,
      num_infants: `${search.num_infants}`,
      outbound_airline_iata_code: airlines.entities[firstOutboundSegment.operating_airline_id].code,
      outbound_arrival_airport: outboundEndLocation,
      selected_fare_family_name: fareFamily,
      fare_families_exist: selectedFlight.itinerary.fare_families_exist,
      outbound_departure_airport: outboundStartLocation,
      outbound_departure_datetime: firstOutboundSegment.start_datetime,
      outbound_flight_code: firstOutboundSegment.marketing_flight_number,
      search_reference_id: searchReferenceId,
      supplier: itinerary.supplier,
      total_price: itinerary.price.amount,
    };
  }

  public viewFlightSubBooking$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromActions.VIEW_FLIGHT_SUB_BOOKING),
      withLatestFrom(this.ngrxStore),
      switchMap(([, state$]) => {
        const state = state$['travel-booking'].travelFlightSubBooking;
        return this.viewSubBooking(state);
      })
    )
  );

  /**
   * Either gets the sub booking info form the state if it's present or makes an API call to get it
   *
   * @param state
   */
  public viewSubBooking(state: fromReducers.FlightSubBookingState): Observable<any> {
    if (state && state !== fromReducers.initialState) {
      return of(new fromActions.ViewFlightSubBookingSuccess(state.subBooking));
    } else {
      const json = localStorage.getItem(TRAVEL_FLIGHT_SUB_BOOKING_LOCAL_STORAGE_KEY);

      if (json !== null) {
        const parsedJson = JSON.parse(json);
        const endPoint =
          this.fareFamilySwitch && parsedJson.fare_families_exist
            ? ApiEndpoint.TRAVEL_FLIGHT_FARE_FAMILY_SUB_BOOKING
            : ApiEndpoint.TRAVEL_FLIGHT_SUB_BOOKING;
        const flightSubBookingId = parsedJson.flight_sub_booking_id || parsedJson.id;

        const url = UrlLib.replaceTemplateKeys(endPoint, { id: flightSubBookingId });

        return this.http.get<FlightSubBookingResponse>(url).pipe(
          map((subBooking) => new fromActions.ViewFlightSubBookingSuccess(subBooking)),
          catchError((err) => of(new fromActions.ViewFlightSubBookingFailure(err)))
        );
      } else {
        return of(new fromActions.ViewFlightSubBookingSuccess(fromReducers.initialState.subBooking));
      }
    }
  }

  /**
   * Save the sub booking response to local storage and update the local booking
   * @param data
   */
  public createSubBookingSuccess(data: FlightSubBookingResponse) {
    const flight_sub_booking_id = UrlLib.extractId(data.url ? data.url : '');
    const fare_families_exist = !!data?.fare_family_name && this.fareFamilySwitch;

    localStorage.setItem(
      TRAVEL_FLIGHT_SUB_BOOKING_LOCAL_STORAGE_KEY,
      JSON.stringify({ flight_sub_booking_id, fare_families_exist })
    );
    this.ngrxStore.dispatch(
      new travelBookingProgressBarActions.UpdateTravelBookingBar({ is_flight_options_complete: true })
    );
  }

  private routeToNextPage() {
    this.ngrxStore
      .select(GetTravelBookingBarState)
      .pipe(take(1))
      .subscribe((value) => {
        if (value.is_hotel_search_complete) {
          this.router.navigate(['/travel/hotel-results/']);
        } else if (value.is_car_hire_search_complete) {
          this.router.navigate(['/travel/car-hire-results/']);
        } else {
          this.router.navigate([`/booking/travel/`]);
        }
      });
  }
}
